import { useEffect, useRef } from 'react';
import { Alert, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import type { CaptiveAccountState, CaptiveActivationOptions } from '../api/captive';
import { formatRubles, isPositiveMoney } from './format';
import { PlanChoice } from './PlanChoice';
import { TopUpRoutes } from './TopUpRoutes';

/**
 * How often the screen re-reads the account state on its own (ms).
 *
 * Every poll is a /captive/account call, which costs the core about eight database
 * round trips, and EVERY device in the walled garden can have this screen open — so
 * the interval is a load decision, not a UX one: ~300 garden devices at 30 s is
 * ~600 requests/min, comfortably inside the portal's listener-wide limit, while
 * still catching an office/ОСМП payment about as fast as a subscriber can walk back
 * to his phone. The poll additionally pauses while the page is hidden and never
 * overlaps an in-flight refresh.
 */
const REFRESH_INTERVAL_MS = 30_000;

/**
 * Self-activation screen — the `awaiting_activation` branch of the portal flow.
 *
 * A brand-new account is created switched off (status 10) and gets a walled-garden
 * address, so this portal page is the ONLY page its subscriber can reach. The
 * screen therefore has to answer, on a phone, three questions at once: what the
 * tariff will charge to start, what is on the account now, and what is missing.
 *
 * Two exits:
 *   can_activate  → [Активировать] → POST /captive/activate (the core then drops the
 *                   captive session, so the device re-requests its address at once)
 *   otherwise     → the two top-up routes: a card payment (a Phase-3 stub that
 *                   answers "unavailable" — presented calmly, never as an error) and
 *                   the office/ОСМП route with [Обновить], which re-reads
 *                   GET /captive/account so the screen flips to [Активировать] the
 *                   moment the payment lands. The re-read also runs on a timer.
 *
 * Every amount is a decimal string from the core (money.Amount) and is rendered by
 * formatRubles() — nothing here parses money into a float.
 */
export function ActivationScreen({
  account,
  registrationConfirmed = true,
  activating,
  refreshing,
  topUpPending,
  error,
  topUpNotice,
  options,
  optionsLoading,
  selectedTariff,
  onSelectTariff,
  onActivate,
  onTopUp,
  onRefresh,
  onRelogin,
}: {
  account: CaptiveAccountState;
  /**
   * ⚠ FALSE SINCE D408 (2026-09-01). This screen opens «Устройство зарегистрировано.
   * Осталось активировать услугу» and closes with the same claim, both of which were
   * true for as long as the screen was reachable only through a SUCCESSFUL
   * registration. Since the core answers `200 outcome:"no_device_identity"` for any
   * ISP address with no captive lease behind it, a never-activated subscriber holding
   * a static or otherwise non-garden address reaches this screen with nothing bound —
   * and the activation is still exactly what he must do, so the screen is right and
   * only its two device sentences are wrong. They are swapped, not dropped: telling
   * him nothing at all about the device is what makes a subscriber re-register in a
   * loop, which is the reason those sentences exist.
   */
  registrationConfirmed?: boolean;
  activating: boolean;
  refreshing: boolean;
  topUpPending: boolean;
  /**
   * The plan list, when this account activates out of a tariff group. Null while it
   * is still loading, or when no choice applies.
   */
  options: CaptiveActivationOptions | null;
  optionsLoading: boolean;
  selectedTariff: number | null;
  onSelectTariff: (tariffID: number) => void;
  /** Activation failure copy (incl. the insufficient-balance figure). */
  error: string | null;
  /** Calm notice about the card-payment route (503 stub / transport trouble). */
  topUpNotice: string | null;
  onActivate: () => void;
  onTopUp: () => void;
  onRefresh: () => void;
  onRelogin: () => void;
}) {
  const { t } = useTranslation();

  // Poll the account state while the screen is open so an office/ОСМП payment shows
  // up without the subscriber doing anything. Kept in a ref so a re-render (a fresh
  // callback identity) never restarts the interval. A tick is skipped while the page
  // is hidden (nobody is watching) or while a refresh is already in flight (a slow
  // core must not accumulate a queue of polls).
  const refreshRef = useRef(onRefresh);
  refreshRef.current = onRefresh;
  const busyRef = useRef(refreshing);
  busyRef.current = refreshing;
  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.hidden || busyRef.current) return;
      refreshRef.current();
    }, REFRESH_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, []);

  // ⚠ WHEN A PLAN MUST BE CHOSEN, EVERY FIGURE ON THIS SCREEN BELONGS TO THE CHOSEN
  // PLAN — not to the account block. The account block's `activation_charge` is 0 and
  // its `can_activate` is false for such an account BY CONSTRUCTION (there is no tariff
  // to price, so the core reports the charge as UNKNOWN and fails the gate closed), so
  // rendering it here would show «Списание при активации: Без списания» beside a button
  // that cannot work. The per-plan figures come from the options call, decided by the
  // core's one gate arithmetic against one money read.
  const choosing = account.tariff_choice_required;
  const plan = choosing
    ? (options?.options.find((o) => o.tariff_id === selectedTariff) ?? null)
    : null;
  const canActivate = choosing ? (plan?.can_activate ?? false) : account.can_activate;

  const shortfallSource = plan ? plan.shortfall : account.shortfall;
  const requiredSource = plan ? plan.required : account.required;

  const shortfall = isPositiveMoney(shortfallSource);
  // `required` is max(min_balance, activation_charge) and can legitimately be 0 (no
  // configured minimum, nothing to charge) while a NEGATIVE balance still leaves a
  // shortfall. Showing "Нужно на счету 0.00 ₽" beside "Не хватает 100.00 ₽" reads as
  // a contradiction, so a zero requirement is simply not shown and the copy below
  // names the debt instead.
  const required = isPositiveMoney(requiredSource);
  const charge = plan
    ? plan.charge_known
      ? formatRubles(plan.activation_charge)
      : t('planChoice.priceUnknown')
    : isPositiveMoney(account.activation_charge)
      ? formatRubles(account.activation_charge)
      : t('activation.chargeNone');

  return (
    <>
      <p className="portal-title">{t('activation.title')}</p>
      <p className="portal-subtitle">
        {t(registrationConfirmed ? 'activation.subtitle' : 'activation.subtitleUnconfirmed')}
      </p>

      {choosing && (
        <PlanChoice
          options={options}
          loading={optionsLoading}
          selected={selectedTariff}
          onSelect={onSelectTariff}
        />
      )}

      <div className="identity-panel" style={{ marginBottom: 16, textAlign: 'left' }}>
        <span className="group-label">{t('activation.detailsLabel')}</span>
        {(plan?.name ?? account.tariff_name) && (
          <div className="identity-row">
            <span className="k">{t('activation.tariff')}</span>
            <span className="v">{plan?.name ?? account.tariff_name}</span>
          </div>
        )}
        {/* Until a plan is picked there is no charge to name, and «Без списания» would
            be a false answer rather than a missing one. */}
        {(!choosing || plan) && (
          <div className="identity-row">
            <span className="k">{t('activation.charge')}</span>
            <span className="v money">{charge}</span>
          </div>
        )}
        <div className="identity-row">
          <span className="k">{t('activation.balance')}</span>
          <span className="v money">{formatRubles(account.balance)}</span>
        </div>
        {required && (
          <div className="identity-row">
            <span className="k">{t('activation.required')}</span>
            <span className="v money">{formatRubles(requiredSource)}</span>
          </div>
        )}
        {shortfall && (
          <div className="identity-row">
            <span className="k">{t('activation.shortfall')}</span>
            <span className="v money money-alert">{formatRubles(shortfallSource)}</span>
          </div>
        )}
      </div>

      {error && (
        <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} role="alert" />
      )}

      {choosing && !plan && (options?.options.length ?? 0) > 0 && (
        <Alert type="info" showIcon message={t('planChoice.selectFirst')} style={{ marginBottom: 16 }} />
      )}

      {canActivate ? (
        <>
          <Alert type="info" showIcon message={t('activation.ready')} style={{ marginBottom: 16 }} />
          <Button type="primary" block loading={activating} onClick={onActivate}>
            {t('activation.activate')}
          </Button>
        </>
      ) : choosing && !plan ? null : (
        <>
          <Alert
            type="warning"
            showIcon
            message={t('activation.needMoneyTitle')}
            description={
              required
                ? t('activation.needMoney', {
                    required: formatRubles(requiredSource),
                    balance: formatRubles(account.balance),
                  })
                : t('activation.needMoneyDebt', { shortfall: formatRubles(shortfallSource) })
            }
            style={{ marginBottom: 16 }}
          />
          <TopUpRoutes
            topUpPending={topUpPending}
            refreshing={refreshing}
            topUpNotice={topUpNotice}
            onTopUp={onTopUp}
            onRefresh={onRefresh}
          />
        </>
      )}

      <p className="portal-hint" style={{ margin: '16px 0 0', textAlign: 'center' }}>
        {t(registrationConfirmed ? 'activation.deviceRegistered' : 'activation.deviceUnconfirmed')}
      </p>
      <div className="portal-center">
        <Button type="link" onClick={onRelogin}>
          {t('activation.relogin')}
        </Button>
      </div>
    </>
  );
}
